import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useLogout, useMe } from '../api/queries/auth.ts'
import { GuestWorkPrompt } from '../features/auth/GuestWorkPrompt.tsx'
import { KeepWorkBanner } from '../features/auth/KeepWorkBanner.tsx'
import { ToolMark } from '../features/hub/ToolArt.tsx'
import { useTools } from '../features/hub/tools.tsx'
import { defineMessages, useT } from '../i18n/index.ts'
import { LanguageSwitcher } from '../i18n/LanguageSwitcher.tsx'
import { cn } from '../lib/cn.ts'
import { Button, buttonClass } from '../ui/Button.tsx'
import { IconCaret, IconChart, IconTools, IconUser } from '../ui/Icon.tsx'
import { Logo } from '../ui/Logo.tsx'

const messages = defineMessages({
	en: {
		tools: 'Tools',
		results: 'Results',
		allTools: 'All tools',
		skip: 'Skip to content',
		signIn: 'Sign in',
		save: 'Save your work',
		saveShort: 'Save',
		signOut: 'Sign out',
		footer: 'One account, every tool. Sources stay yours.',
		licenses: 'Licenses',
	},
	ro: {
		tools: 'Instrumente',
		results: 'Rezultate',
		allTools: 'Toate instrumentele',
		skip: 'Salt la conținut',
		signIn: 'Autentificare',
		save: 'Salvează-ți lucrul',
		saveShort: 'Salvează',
		signOut: 'Ieșire',
		footer: 'Un singur cont, toate instrumentele. Sursele rămân ale tale.',
		licenses: 'Licențe',
	},
	ru: {
		tools: 'Инструменты',
		results: 'Результаты',
		allTools: 'Все инструменты',
		skip: 'Перейти к содержимому',
		signIn: 'Войти',
		save: 'Сохранить работу',
		saveShort: 'Сохранить',
		signOut: 'Выйти',
		footer: 'Один аккаунт для всех инструментов. Материалы остаются вашими.',
		licenses: 'Лицензии',
	},
	fr: {
		tools: 'Outils',
		results: 'Résultats',
		allTools: 'Tous les outils',
		skip: 'Aller au contenu',
		signIn: 'Se connecter',
		save: 'Enregistrer votre travail',
		saveShort: 'Enregistrer',
		signOut: 'Se déconnecter',
		footer: 'Un seul compte pour tous les outils. Vos sources restent à vous.',
		licenses: 'Licences',
	},
	it: {
		tools: 'Strumenti',
		results: 'Risultati',
		allTools: 'Tutti gli strumenti',
		skip: 'Vai al contenuto',
		signIn: 'Accedi',
		save: 'Salva il tuo lavoro',
		saveShort: 'Salva',
		signOut: 'Esci',
		footer: 'Un solo account, tutti gli strumenti. Le fonti restano tue.',
		licenses: 'Licenze',
	},
	kk: {
		tools: 'Құралдар',
		results: 'Нәтижелер',
		allTools: 'Барлық құралдар',
		skip: 'Мазмұнға өту',
		signIn: 'Кіру',
		save: 'Жұмысты сақтау',
		saveShort: 'Сақтау',
		signOut: 'Шығу',
		footer: 'Барлық құралға бір аккаунт. Материалдар өзіңізде қалады.',
		licenses: 'Лицензиялар',
	},
	es: {
		tools: 'Herramientas',
		results: 'Resultados',
		allTools: 'Todas las herramientas',
		skip: 'Ir al contenido',
		signIn: 'Iniciar sesión',
		save: 'Guarda tu trabajo',
		saveShort: 'Guardar',
		signOut: 'Cerrar sesión',
		footer: 'Una cuenta, todas las herramientas. Tus materiales siguen siendo tuyos.',
		licenses: 'Licencias',
	},
	pt: {
		tools: 'Ferramentas',
		results: 'Resultados',
		allTools: 'Todas as ferramentas',
		skip: 'Ir para o conteúdo',
		signIn: 'Entrar',
		save: 'Salve seu trabalho',
		saveShort: 'Salvar',
		signOut: 'Sair',
		footer: 'Uma conta, todas as ferramentas. Seus materiais continuam seus.',
		licenses: 'Licenças',
	},
	de: {
		tools: 'Werkzeuge',
		results: 'Ergebnisse',
		allTools: 'Alle Werkzeuge',
		skip: 'Zum Inhalt springen',
		signIn: 'Anmelden',
		save: 'Arbeit speichern',
		saveShort: 'Speichern',
		signOut: 'Abmelden',
		footer: 'Ein Konto für alle Werkzeuge. Deine Materialien bleiben deine.',
		licenses: 'Lizenzen',
	},
})

/**
 * The desk's frame: a quiet 60px bar (logo, tools, language, account), the page, a one-line
 * footer. No sidebar. The bar sits on the canvas with a hairline under it, nothing heavier.
 */
export function AppLayout() {
	const t = useT(messages)
	const { data: teacher } = useMe()
	const logout = useLogout()
	const navigate = useNavigate()

	return (
		<div className="flex min-h-dvh flex-col">
			<a
				id="skip-to-content"
				href="#main"
				className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:z-(--z-skip)
					focus:rounded-btn focus:bg-sheet focus:px-4 focus:py-2.5 focus:font-[560] focus:shadow-3"
			>
				{t.skip}
			</a>

			<header className="sticky top-0 z-(--z-sticky) border-b border-line/70 bg-canvas print:hidden">
				<div className="mx-auto flex h-15 w-full max-w-300 items-center gap-1 px-3 sm:gap-2 sm:px-6 lg:px-10">
					<Link
						id="nav-home"
						to="/app"
						viewTransition
						aria-label="Temaly"
						className="-ml-1 flex min-h-11 items-center gap-2.5 rounded-btn px-1 text-[1.0625rem] font-[650] tracking-[-0.01em] text-ink"
					>
						<Logo />
						<span className="hidden sm:inline">Temaly</span>
					</Link>

					<ToolsMenu label={t.tools} allLabel={t.allTools} />
					<ResultsLink label={t.results} />

					<div className="ml-auto flex items-center gap-0.5 sm:gap-2">
						<LanguageSwitcher />
						{teacher?.guest ? (
							// No pressure while she is trying it out: one quiet way to keep her work.
							<>
								<Link
									id="nav-signin"
									to="/login"
									className={cn(buttonClass('quiet', 'sm'), 'max-sm:w-9 max-sm:px-0 max-xs:w-8')}
								>
									{/* A phone has no room for the word beside Save: a person mark, the word for screen readers. */}
									<IconUser className="size-[1.125rem] text-teal-700 sm:hidden" />
									<span className="max-sm:sr-only">{t.signIn}</span>
								</Link>
								<Link
									id="nav-save"
									to="/register"
									aria-label={t.save}
									className={cn(
										buttonClass('secondary', 'sm'),
										'whitespace-nowrap max-sm:px-2.5 max-xs:px-2',
									)}
								>
									<span className="sm:hidden">{t.saveShort}</span>
									<span className="hidden sm:inline">{t.save}</span>
								</Link>
							</>
						) : (
							<>
								{teacher && (
									<span className="hidden max-w-40 truncate px-2 text-small text-ink-2 md:inline">
										{teacher.name}
									</span>
								)}
								<Button
									id="nav-signout"
									variant="quiet"
									size="sm"
									loading={logout.isPending}
									onClick={() => logout.mutate(undefined, { onSuccess: () => navigate('/login') })}
								>
									{t.signOut}
								</Button>
							</>
						)}
					</div>
				</div>
			</header>

			{/*
			 * The page is at least one screen tall, so the footer starts below the first screen and
			 * never shows while a page is still loading. A footer pinned to the bottom of a short
			 * loading state is on screen, then flies out when the content arrives (and back in on the
			 * next route): in Russian, where it wraps to two or three lines on a phone, that one move
			 * was the biggest layout shift on the page. The header and footer are the same elements
			 * across routes (this layout is one route element); only what sits above the footer changes.
			 */}
			<main
				id="main"
				tabIndex={-1}
				className="mx-auto min-h-dvh w-full max-w-[calc(67.5rem+5rem)] flex-1 px-4 pb-20 pt-8 outline-none sm:px-6 sm:pt-12 lg:px-10 print:min-h-0 print:max-w-none print:p-0"
			>
				<div className="mb-6 empty:hidden print:hidden">
					<KeepWorkBanner />
				</div>
				<Outlet />
			</main>
			<GuestWorkPrompt />

			<footer className="mx-auto w-full max-w-300 px-4 pb-8 text-small text-ink-2 sm:px-6 lg:px-10 print:hidden">
				<span className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
					{t.footer}
					<a
						id="footer-licenses"
						href="/third-party-licenses.txt"
						className="underline-offset-2 hover:text-ink hover:underline"
					>
						{t.licenses}
					</a>
				</span>
			</footer>
		</div>
	)
}

/** Next to Tools, same size and style: every quiz she gave, group by group (/results). */
function ResultsLink({ label }: { label: string }) {
	return (
		<NavLink
			id="nav-results"
			to="/results"
			viewTransition
			aria-label={label}
			className={({ isActive }: { isActive: boolean }) =>
				cn(
					buttonClass('quiet', 'sm'),
					'gap-1.5 px-2.5 text-ink max-sm:px-2 max-xs:px-1.5',
					isActive && 'bg-teal-50',
				)
			}
		>
			<IconChart className="size-5 text-teal-700 sm:size-[1.125rem]" />
			{/* Mark only on a phone, like Tools beside it. */}
			<span className="hidden sm:inline">{label}</span>
		</NavLink>
	)
}

/**
 * Chrome before 114 (the last on Windows 7 and 8 is 109) and Safari before 17 have no popover. There
 * a `popover` panel is neither hidden nor opened: invisible yet over the page, it took clicks meant
 * for the page. On those the menu opens and closes by state instead.
 */
const nativePopover = typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype

/**
 * Every tool, one click from anywhere. A native popover: Esc, outside click and the top layer
 * come from the browser. Under a dropdown on desktop, a sheet sliding up from the bottom on
 * phones (CSS, `.tools-menu` in index.css).
 */
function ToolsMenu({ label, allLabel }: { label: string; allLabel: string }) {
	const tools = useTools()
	const panel = useRef<HTMLDivElement>(null)
	const opener = useRef<HTMLButtonElement>(null)
	// Only without a native popover; with one, the browser owns open and closed.
	const [open, setOpen] = useState(false)
	const close = () => (nativePopover ? panel.current?.hidePopover() : setOpen(false)) // old-browsers: ok, nativePopover

	// Without a native popover: what its light dismiss would do, a press outside or Esc closes it.
	useEffect(() => {
		if (nativePopover || !open) return
		panel.current?.querySelector<HTMLElement>('a')?.focus()
		const away = (e: PointerEvent) => {
			const at = e.target as Node
			if (!panel.current?.contains(at) && !opener.current?.contains(at)) setOpen(false)
		}
		const esc = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return
			// Like the native popover: focus comes back only from inside the menu.
			const inside = panel.current?.contains(document.activeElement)
			setOpen(false)
			if (inside) opener.current?.focus()
		}
		document.addEventListener('pointerdown', away)
		document.addEventListener('keydown', esc)
		return () => {
			document.removeEventListener('pointerdown', away)
			document.removeEventListener('keydown', esc)
		}
	}, [open])

	return (
		<>
			<button
				ref={opener}
				id="nav-tools"
				type="button"
				{...(nativePopover
					? { popoverTarget: 'tools-menu' }
					: {
							onClick: () => setOpen((v) => !v),
							'aria-expanded': open,
							'aria-controls': 'tools-menu',
						})}
				aria-label={label}
				className={cn(
					buttonClass('quiet', 'sm'),
					'gap-1.5 px-2.5 text-ink max-sm:px-2 max-xs:px-1.5',
				)}
			>
				<IconTools className="size-5 text-teal-700 sm:size-[1.125rem]" />
				{/* A phone gets the mark only: with the label the guest bar ran 455px wide in Russian. */}
				<span className="hidden sm:inline">{label}</span>
				<IconCaret className="hidden size-3.5 text-ink-3 sm:block" />
			</button>
			<div
				ref={panel}
				id="tools-menu"
				{...(nativePopover
					? { popover: 'auto' as const }
					: { hidden: !open, 'data-open': open || undefined })}
				aria-label={label}
				onToggle={(e) => {
					if ((e as unknown as ToggleEvent).newState === 'open')
						panel.current?.querySelector<HTMLElement>('a')?.focus()
				}}
				className="tools-menu m-0 w-[min(26rem,calc(100vw-2rem))] rounded-btn bg-sheet p-2 text-ink shadow-3"
			>
				<span
					aria-hidden="true"
					className="mx-auto mb-2 mt-1 block h-1 w-10 rounded-full bg-line sm:hidden"
				/>
				<ul className="flex flex-col">
					{tools.map((tool) => (
						<li key={tool.id}>
							<NavLink
								id={`menu-tool-${tool.id}`}
								to={tool.to ?? '/app'}
								viewTransition
								onClick={close}
								className={({ isActive }: { isActive: boolean }) =>
									cn(
										'flex min-h-14 items-center gap-3 rounded-input px-2.5 py-2 transition-colors duration-(--dur-instant) hover:bg-teal-50 focus-visible:bg-teal-50',
										isActive && 'bg-teal-50',
									)
								}
							>
								<ToolMark tool={tool.key} icon={tool.icon} size="sm" />
								<span className="flex min-w-0 flex-col">
									<span className="font-[600] leading-snug">{tool.short}</span>
									<span className="truncate text-small text-ink-2">{tool.line}</span>
								</span>
							</NavLink>
						</li>
					))}
				</ul>
				<div className="mt-1 border-t border-line px-1 pt-1">
					<Link
						id="menu-all-tools"
						to="/app"
						viewTransition
						onClick={close}
						className="flex min-h-11 items-center rounded-input px-2 text-small font-[560] text-teal-700 hover:bg-teal-50"
					>
						{allLabel}
					</Link>
				</div>
			</div>
		</>
	)
}
